import chunk from 'lodash/chunk';
import { runInAction, when } from 'mobx';
import ProcessoApi from '../api/ProcessoApi';
import { IProcessoDto } from '../api/dto/ProcessoDto';
import { IEtiqueta, IIndicadores, ISearch, Minuta, Processo, TipoMinuta } from '../modelo';
import { Pauta } from '../modelo/Pauta';
import UsuarioStore from '../store/UsuarioStore';
import FiltroStore from '../store/FiltroStore';
import GabineteStore from '../store/GabineteStore';
import MensagemStore from '../store/MensagemStore';
import MovimentacaoExternaStore from '../store/MovimentacaoExternaStore';
import ProcessoStore from '../store/ProcessoStore';
import RouterStore from '../store/RouterStore';
import Ambiente from '../utils/Ambiente';
import { apiErroPadraoJaExibido, apresentarApiErroPadrao } from '../utils/ApiErroPadraoUtils';
import NumeroProcesso from '../utils/NumeroProcesso';
import UtilNumeroProcesso from '../utils/UtilNumeroProcesso';
import AssuntoService from './AssuntoService';
import ComentariosMinutaService from './ComentariosMinutaService';
import FiltroService from './FiltroService';
import InformacoesProcessoService from './InformacoesProcessoService';
import MinutaService from './MinutaService';
import Service from './Service';
import MapeadorProcesso from './mapeadores/MapeadorProcesso';
import MapeadorProcessoAssinatura from './mapeadores/MapeadorProcessoAssinatura';
import { RespostaPaginada } from './parametros/RespostaPaginada';
import { ProcessoAssociado, ProcessoComCorreJunto } from '@/modelo/ProcessoRelacionamentos';
import TriagemService from './TriagemService';

const QTD_PROCESSOS_INICIAIS = 20;
const DESCRICAO_INDICADOR_SEGREDO_DE_JUSTICA = 'Segredo de Justiça';

class ProcessoService extends Service {
    indiCarregados = false;
    assutoscarregados = false;
    private readonly tamanhoLoteFiltroRapido = 1000;
    // Dispara os lotes de /filtro-rapido2 com concorrência limitada: gabinetes com muitos
    // processos geram dezenas de lotes, e disparar todos de uma vez sobrecarrega o backend e
    // aumenta a chance de algum lote estourar o timeout de 120s (AxiosError "timeout of
    // 120000ms exceeded" em /escaninho/gerencial).
    private readonly concorrenciaMaximaLotes = 6;

    dadosProcessosAtualizados = {};

    /** Limita quantas tarefas assíncronas rodam em paralelo, sem bloquear a ordem de disparo. */
    private criarLimitadorDeConcorrencia(maximo: number) {
        let ativos = 0;
        const fila: (() => void)[] = [];
        const liberar = () => {
            ativos--;
            const proximo = fila.shift();
            if (proximo) proximo();
        };
        return async <T>(tarefa: () => Promise<T>): Promise<T> => {
            if (ativos >= maximo) {
                await new Promise<void>(resolve => fila.push(resolve));
            }
            ativos++;
            try {
                return await tarefa();
            } finally {
                liberar();
            }
        };
    }

    /**
     * Busca um valor por processo em lotes (evita URLs/requisições grandes demais) e
     * mescla os resultados dos lotes num único mapa idProcesso -> valor.
     */
    private async buscarEmLotes<T>(
        idsProcessos: number[],
        apiCall: (lote: number[]) => Promise<any>,
        tamanhoLote = 900
    ): Promise<Record<number, T>> {
        const lotes = chunk(idsProcessos, tamanhoLote);
        const respostas = await Promise.all(lotes.map(lote => apiCall(lote)));
        let resultado: Record<number, T> = {};
        respostas.forEach(r => {
            resultado = { ...resultado, ...r };
        });
        return resultado;
    }

    /** Aplica um valor (já carregado por processo) a todos os processos da store. */
    private aplicarATodosOsProcessos<T>(valores: Record<number, T>, aplicar: (processo: Processo, valor: T) => void): void {
        runInAction(() => {
            ProcessoStore._processos.forEach(processo => {
                aplicar(processo, valores[processo.identificadorProcesso]);
            });
        });
    }

    /** Aplica um valor (já carregado por processo) apenas aos processos cujo id está em idsProcessos. */
    private aplicarAosProcessos<T>(idsProcessos: number[], valores: Record<number, T>, aplicar: (processo: Processo, valor: T) => void): void {
        runInAction(() => {
            ProcessoStore._processos
                .filter(proc => idsProcessos.includes(proc.identificadorProcesso))
                .forEach(processo => aplicar(processo, valores[processo.identificadorProcesso]));
        });
    }

    /**
     * O sigilo (segredo de justiça) é um atributo do processo (`processo.segredoDeJustica`),
     * não um indicador retornado pelo endpoint de indicadores. Para que processos PJe em
     * segredo de justiça também sejam encontrados pelo filtro de Indicadores/Chips (que
     * opera sobre `processo.indicadores`, comparando por `descricao`), garante aqui uma
     * única descrição canônica "Segredo de Justiça": normaliza a variação de grafia que
     * já vem do backend para o eSIJ (ex.: "SEGREDO DE JUSTIÇA") e sintetiza o indicador
     * quando o atributo estiver marcado mas ainda não tiver vindo do backend (caso do PJe).
     * Isso não altera a lógica de filtragem/agrupamento por `descricao` já existente.
     */
    private comIndicadorSegredoDeJustica(processo: Processo, indicadores: IIndicadores[]): IIndicadores[] {
        const indicadoresAtuais = indicadores || [];
        const indiceExistente = indicadoresAtuais.findIndex(ind =>
            ind.descricao?.toLocaleUpperCase('pt-BR') === DESCRICAO_INDICADOR_SEGREDO_DE_JUSTICA.toLocaleUpperCase('pt-BR'));

        if (indiceExistente >= 0) {
            if (indicadoresAtuais[indiceExistente].descricao === DESCRICAO_INDICADOR_SEGREDO_DE_JUSTICA) {
                return indicadoresAtuais;
            }
            const indicadoresNormalizados = [...indicadoresAtuais];
            indicadoresNormalizados[indiceExistente] = { ...indicadoresNormalizados[indiceExistente], descricao: DESCRICAO_INDICADOR_SEGREDO_DE_JUSTICA };
            return indicadoresNormalizados;
        }

        if (!processo.segredoDeJustica) {
            return indicadoresAtuais;
        }
        return [...indicadoresAtuais, {
            descricao: DESCRICAO_INDICADOR_SEGREDO_DE_JUSTICA,
            id: undefined,
            origem: undefined,
            isEtiqueta: false
        } as IIndicadores];
    }

    async carregarSituacaoDePautaTodosOsProcessosAsync() {
        runInAction(() => {
            ProcessoStore.carregandoSituacaoPauta = true;
        });

        InformacoesProcessoService.buscarSituacaoInclusaoPautaDaListaDeProcessos(this.idsProcessos).then(situacao => {
            runInAction(() => {
                ProcessoStore._processos.forEach(processo => {
                    processo.situacaoDeInclusaoEmPauta = situacao[processo.identificadorProcesso] || [];
                });
                ProcessoStore.carregandoSituacaoPauta = false;
                ProcessoStore.carregouSituacaoPauta = true;
            });
        });
    }

    async carregarMovimentosProcessosPjeAsync(): Promise<void> {
        if (ProcessoStore.carregouMovimentosPje || ProcessoStore.carregandoMovimentosPje) return;
        runInAction(() => {
            ProcessoStore.carregandoMovimentosPje = true;
        });
        try {
            const processosPje = ProcessoStore._processos.filter(p => p.ehPje);
            await this.carregarMovimentosProcessosPje(processosPje);
        } finally {
            runInAction(() => {
                ProcessoStore.carregandoMovimentosPje = false;
                ProcessoStore.carregouMovimentosPje = true;
            });
        }
    }

    async carregarMovimentosProcessosPjePorIdsProcessos(idsProcessos: number[]): Promise<void> {
        const processosPje = ProcessoStore._processos
            .filter(p => idsProcessos.includes(p.identificadorProcesso) && p.ehPje);
        await this.carregarMovimentosProcessosPje(processosPje);
    }

    private async carregarMovimentosProcessosPje(processos: Processo[], tamanhoLote = 15): Promise<void> {
        const processosParaCarregar = processos.filter(p => !p.movimentos?.length);
        if (processosParaCarregar.length === 0) return;

        /*let movimentos = {};
        const listaIds = chunk(processosParaCarregar.map(proc => proc.identificadorProcesso), 900);
        const promises = listaIds.map(lista => InformacoesProcessoService.consultarMovimentosEmLote(lista));
        await Promise.all(promises).then(result => {
            result.forEach(r => movimentos = { ...movimentos, ...r });
        });*/

        processos.forEach(processo => {
            runInAction(() => {
                processo.movimentos = [];

                /*processo.movimentos = movimentos[processo.identificadorProcesso] || [];
                const maisRecente = [...processo.movimentos].sort((m1, m2) => {
                    const d1 = m1?.atualizadoEm ? new Date(m1.atualizadoEm).getTime() : 0;
                    const d2 = m2?.atualizadoEm ? new Date(m2.atualizadoEm).getTime() : 0;
                    return d2 - d1;
                })[0];
                if (maisRecente) {
                    processo.ultimoMovimentoProcessual = maisRecente;
                }*/
            });
        });

        // for (let i = 0; i < processosParaCarregar.length; i += tamanhoLote) {
        //     const lote = processosParaCarregar.slice(i, i + tamanhoLote);
        //     const resultados = await Promise.allSettled(
        //         const movimentos = await InformacoesProcessoService.consultarMovimentos(lote.map(p => p.identificadorProcesso));
        //         // lote.map(async (processo) => {
        //         //     try {
        //         //         const movimentos = await InformacoesProcessoService.consultarMovimentos(processo.identificadorProcesso);
        //         //         return { processo, movimentos };
        //         //     } catch (e) {
        //         //         return { processo, movimentos: [] };
        //         //     }
        //         // })
        //     );

        // runInAction(() => {
        //     resultados.forEach(res => {
        //         if (res.status === 'fulfilled' && res.value?.movimentos?.length > 0) {
        //             const { processo, movimentos } = res.value;
        //             processo.movimentos = movimentos;
        //             const maisRecente = [...movimentos].sort((m1, m2) => {
        //                 const d1 = m1?.atualizadoEm ? new Date(m1.atualizadoEm).getTime() : 0;
        //                 const d2 = m2?.atualizadoEm ? new Date(m2.atualizadoEm).getTime() : 0;
        //                 return d2 - d1;
        //             })[0];
        //             if (maisRecente) {
        //                 processo.ultimoMovimentoProcessual = maisRecente;
        //             }
        //         }
        //     });
        // });
        // }
    }

    async carregarUltimaInclusaoEmPautaTodosOsProcessosAsync() {
        this.buscarUltimaInclusaoEmPauta(this.idsProcessos).then(ultimaPauta => {
            runInAction(() => {
                ProcessoStore._processos.forEach(processo => {
                    processo.pauta = ultimaPauta[processo.identificadorProcesso];
                })
            });
        });
    }

    async buscarUltimaInclusaoEmPauta(_idsProcessos: number[]) {
        return this.buscarEmLotes<Pauta>(this.idsProcessos, lote => ProcessoApi.buscarUltimaInclusaoEmPauta(lote));
    }

    async carregarResultadoJulgamentoDeTodosOsProcessosAsync() {
        runInAction(() => {
            ProcessoStore.carregandoResultadosJulgamento = true;
        });
        const listaIdsProcesso = chunk(this.idsProcessos, 900);
        const promises = listaIdsProcesso.map(ids => InformacoesProcessoService.buscarResultadoDeJulgamentoDaListaDeProcessos(ids));
        const idsDeProcessoComVoto = ProcessoStore._processos.filter(proc =>
            proc.minutas.some(minuta => {
                return minuta.tipo.codigo === TipoMinuta.VOTO;
            })
        ).map(proc => proc.identificadorProcesso);
        const chunkidsDeProcessoComVoto = chunk(idsDeProcessoComVoto, 900);
        const promisesAgProvido = chunkidsDeProcessoComVoto.map(ids => InformacoesProcessoService.buscarProcessosComAgProvido(ids));
        await Promise.all(promisesAgProvido).then(result => {
            let resultados = [];
            result.forEach(r => resultados = { ...resultados, ...r });
            runInAction(() => {
                ProcessoStore._processos.forEach(processo => {
                    processo.agravoProvido = resultados[processo.identificadorProcesso];
                });
            });
        });


        await Promise.all(promises).then(result => {
            let resultados = [];
            result.forEach(r => resultados = { ...resultados, ...r });
            runInAction(() => {
                ProcessoStore._processos.forEach(processo => {
                    processo.resultadoJulgamentoProcesso = resultados[processo.identificadorProcesso] || [];
                })
                ProcessoStore.carregandoResultadosJulgamento = false;
                ProcessoStore.carregouResultadosJulgamento = true;
            });
        });
    }

    async carregaImpedimentos(idProcesso: number) {
        const impedimentos = await ProcessoApi.buscarImpedimentosNoProcesso(idProcesso);
        let processo = ProcessoStore._processos.find(procStore => procStore.identificadorProcesso === idProcesso);
        if (!processo) {
            processo = await ProcessoApi.recuperaProcessoPorId(idProcesso)
        }
        runInAction(() => {
            processo.impedimentos = impedimentos;
        });
    }

    devolverProcesso(processo: Processo) {
        const observacao = MovimentacaoExternaStore.observacao;
        const codMotAnd = MovimentacaoExternaStore.motivoAndamento;
        if (processo.ehLegado && !codMotAnd) {
            MensagemStore.apresentarAviso("Favor informar motivo da devolução.");
            runInAction(() => {
                MovimentacaoExternaStore.erroFaltaMotAnd = true;
            });
            return;
        }
        return ProcessoApi.devolverProcesso(processo, observacao, codMotAnd);
    }

    async atualizarConteudoDaMinuta(idProcesso, numMinuta) {
        return await ProcessoApi.atualizarConteudoDaMinutaDoProcesso(idProcesso, numMinuta);
    }

    async atualizarDadosDoProcesso(idProcesso) {
        return await ProcessoApi.atualizarDadosDoProcesso(idProcesso);
    }

    async associarProcessoAServidor(orgao: string, cpf: string, proximoCard: number, processos: Processo[], observacao: string) {
        return await ProcessoApi.associarProcessoAServidor(orgao, cpf, proximoCard, processos, observacao);
    }

    // async importarProcessos(orgaoJulgador?) {
    //     let oj = orgaoJulgador || UsuarioStore.gabinete;
    //     return await ProcessoApi.importarProcessos(oj);
    // }

    async processoPorIdFiltroRapido(orgaoJulgador: string, idProcesso: number, search?: ISearch): Promise<Processo[]> {
        search = {
            gabinete: UsuarioStore.gabinete,
            idsProcessos: [idProcesso],
            ...search
        }
        const processosComMinuta = await ProcessoApi.recuperaPorSearch({ ...search, apenasProcessos: false });
        const listaFinal = (processosComMinuta || []);

        return MapeadorProcesso.toProcessos(listaFinal);
    }

    async processoPorIdsFiltroRapido(orgaoJulgador: string, idsProcesso: number[], search?: ISearch): Promise<Processo[]> {
        search = {
            gabinete: UsuarioStore.gabinete,
            idsProcessos: idsProcesso,
            ...search
        }
        const processosComMinuta = await ProcessoApi.recuperaPorSearch({ ...search, apenasProcessos: false });
        const listaFinal = (processosComMinuta || []);

        return MapeadorProcesso.toProcessos(listaFinal);
    }

    async buscarProcessosPautasFuturas(orgaoJulgador: string): Promise<IProcessoDto[]> {
        return await ProcessoApi.buscarProcessosPautasFuturas(orgaoJulgador);
    }

    async processoPorIdEscaninhoPeticao(idProcesso) {
        const search: ISearch = {
            gabinete: UsuarioStore.gabinete,
            idsProcessos: [idProcesso]
        }
        const processosComMinuta = await ProcessoApi.recuperaPorSearch({ ...search, apenasProcessos: false });
        const listaFinal = (processosComMinuta || []);

        return MapeadorProcesso.toProcessos(listaFinal);

    }

    //MANTEM
    async recuperaProcessoPorId(idProcesso) {
        return await ProcessoApi.recuperaProcessoPorId(idProcesso);
    }

    async recuperaAndamentoInternoNoGabinete(idProcesso, siglaLocal) {
        return await ProcessoApi.recuperaAndamentoInternoNoGabinete(idProcesso, siglaLocal);
    }

    async buscarCorreJuntosEmLote(idsProcessos: number[]): Promise<Record<number, ProcessoComCorreJunto[]>> {
        return this.buscarEmLotes<ProcessoComCorreJunto[]>(idsProcessos, lote => ProcessoApi.recuperaProcessosComCorreJunto(lote));
    }

    async buscarAssociadosEmLote(idsProcessos: number[]): Promise<Record<number, ProcessoAssociado[]>> {
        return this.buscarEmLotes<ProcessoAssociado[]>(idsProcessos, lote => ProcessoApi.recuperaProcessosAssociados(lote));
    }

    async carregarCorreJuntosDeTodosOsProcessos(): Promise<void> {
        runInAction(() => {
            ProcessoStore.carregandoCorreJuntos = true;
            ProcessoStore.carregouCorreJuntos = false;
        });

        try {
            const processosLegados = ProcessoStore._processos.filter(p => p.ehLegado);
            const idsLegados = processosLegados.map(p => p.identificadorProcesso);

            if (idsLegados.length === 0) {
                runInAction(() => {
                    processosLegados.forEach(p => (p.correJuntos = []));
                    ProcessoStore.carregouCorreJuntos = true;
                });
                return;
            }

            const mapa = await this.buscarCorreJuntosEmLote(idsLegados);

            runInAction(() => {
                processosLegados.forEach(processo => {
                    processo.correJuntos = mapa[processo.identificadorProcesso] || [];
                });
                ProcessoStore.carregouCorreJuntos = true;
            });

        } catch (e) {
            console.error(e);
            if (!apresentarApiErroPadrao(e)) {
                MensagemStore.apresentarErro('Erro ao carregar corre-juntos (processos legados).');
            }
        } finally {
            runInAction(() => {
                ProcessoStore.carregandoCorreJuntos = false;
            });
        }
    }

    async carregarProcessosAssociadosDeTodosOsProcessos(): Promise<void> {
        runInAction(() => {
            ProcessoStore.carregandoProcessosAssociados = true;
            ProcessoStore.carregouProcessosAssociados = false;
        });

        try {
            const processosPje = ProcessoStore._processos.filter(p => p.ehPje);
            const idsPje = processosPje.map(p => p.identificadorProcesso);

            // nada para fazer
            if (idsPje.length === 0) {
                runInAction(() => {
                    processosPje.forEach(p => (p.processosAssociados = []));
                    ProcessoStore.carregouProcessosAssociados = true;
                });
                return;
            }

            const mapa = await this.buscarAssociadosEmLote(idsPje);

            runInAction(() => {

                processosPje.forEach(processo => {
                    processo.processosAssociados = mapa[processo.identificadorProcesso] || [];
                });

                ProcessoStore.carregouProcessosAssociados = true;
            });

        } catch (e) {
            console.error(e);
            if (!apresentarApiErroPadrao(e)) {
                MensagemStore.apresentarErro('Erro ao carregar processos associados (PJe).');
            }
        } finally {
            runInAction(() => {
                ProcessoStore.carregandoProcessosAssociados = false;
            });
        }
    }

    private disposerRelacionamentos?: () => void;

    async carregarRelacionamentosDeTodosOsProcessosAsync(): Promise<void> {
        if (!ProcessoStore.carregouProcessosNoEscaninho) {
            this.disposerRelacionamentos?.();
            this.disposerRelacionamentos = when(
                () => ProcessoStore.carregouProcessosNoEscaninho === true,
                () => {
                    this.disposerRelacionamentos?.();
                    this.disposerRelacionamentos = undefined;
                    this.carregarRelacionamentosDeTodosOsProcessosAsync();
                }
            );
            return;
        }

        await Promise.all([
            this.carregarCorreJuntosDeTodosOsProcessos(),
            this.carregarProcessosAssociadosDeTodosOsProcessos()
        ]);


        //Console.logs
        const _itens = ProcessoStore._processos
            .filter(p =>
                (p.ehPje && (p.processosAssociados?.length ?? 0) > 0) ||
                (p.ehLegado && (p.correJuntos?.length ?? 0) > 0)
            )
            .map(p => {
                if (p.ehPje) {
                    return {
                        id: p.identificadorProcesso,
                        tipo: 'PJe',
                        qtdAssociados: p.processosAssociados.length,
                        exemplo: p.processosAssociados[0]?.numeroProcesso
                    };
                }
                return {
                    id: p.identificadorProcesso,
                    tipo: 'Legado',
                    qtdCorreJuntos: p.correJuntos.length,
                    exemplo: p.correJuntos[0]?.numProcCompletoCorreJunto
                };
            });
    }

    retornaProcessoNaStore(numeroDeProcesso: string): Processo {
        const processo = ProcessoStore._processos.find(proc => NumeroProcesso.valueOf(proc.numeroProcesso).semFase().numeroProcessoFormatado() === numeroDeProcesso);
        return processo;
    }
    async idProcessoPorNumero(numeroDeProcesso): Promise<number> {
        const idProcesso = await ProcessoApi.idProcessoPorNumero(NumeroProcesso.valueOf(numeroDeProcesso).semFase().numeroProcessoFormatado());
        return idProcesso;
    }

    async recuperaPorSearch(search: ISearch, abortController?: AbortController): Promise<Processo[]> {
        const processos = await ProcessoApi.recuperaPorSearch({ ...search, apenasProcessos: false }, abortController?.signal);
        const listaFinal = (processos || []);

        return MapeadorProcesso.toProcessos(listaFinal);

    }

    //MANTEM
    async recuperaPorSearchAsync(search: ISearch, abortController?: AbortController, silent?: boolean, tamanhoPrimeiroLote?: number): Promise<(Promise<IProcessoDto[]>)[]> {
        const idsProcessos = (await ProcessoApi.recuperaIdsProcessosPorSearch(search, abortController?.signal, silent)) as number[];
        // idsProcessos.sort((a, b) => a - b);
        // let idsProcessosPaginados = this.quebraArray(idsProcessos, 900);
        // Lotes menores reduzem chance de 500 no endpoint /filtro-rapido2 em gabinetes com alto volume.
        const idsProcessosPaginados = tamanhoPrimeiroLote
            ? [idsProcessos.slice(0, tamanhoPrimeiroLote), ...chunk(idsProcessos.slice(tamanhoPrimeiroLote), this.tamanhoLoteFiltroRapido)].filter(lote => lote.length)
            : chunk(idsProcessos, this.tamanhoLoteFiltroRapido);
        const buscarLote = (pagina: number[]) => ProcessoApi.recuperaPorSearch(
            { ...search, idsProcessos: pagina }, abortController?.signal, silent
        ) as Promise<IProcessoDto[]>;
        const limitar = this.criarLimitadorDeConcorrencia(this.concorrenciaMaximaLotes);
        if (tamanhoPrimeiroLote && idsProcessosPaginados.length) {
            // Prioriza a primeira página antes de disputar conexões com o restante do acervo.
            const primeiroLote = buscarLote(idsProcessosPaginados[0]);
            const primeiraTentativa = primeiroLote.catch(() => undefined);
            return [primeiroLote, ...idsProcessosPaginados.slice(1).map(pagina =>
                primeiraTentativa.then(() => {
                    if (abortController?.signal.aborted) return [];
                    return limitar(() => buscarLote(pagina));
                })
            )];
        }
        const promises = idsProcessosPaginados.map(pagina => limitar(() => buscarLote(pagina)));

        return promises;
    }

    private quebraArray(array: number[], chunkSize = 10): number[][] {
        const result = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            const chunk = array.slice(i, i + chunkSize);
            result.push(chunk);
        }
        return result;
    }

    removerEtiqueta = async (identificadorProcesso, numeroTag) => {
        await ProcessoApi.removerEtiqueta(identificadorProcesso, numeroTag);
        await this.carregarEtiquetasProcessoPorId(identificadorProcesso);
        await this.atualizarDadosDoProcesso(identificadorProcesso);
    }

    removerEtiquetaEmLote = async (idsProcessos, numerosTags) => {
        await ProcessoApi.removerEtiquetaEmLote(idsProcessos, numerosTags);
        await Promise.all(idsProcessos.map(async idProcesso => {
            await this.carregarEtiquetasProcessoPorId(idProcesso);
            await this.atualizarDadosDoProcesso(idProcesso);
        }));
    }

    async recuperarEtiquetas(identificadorProcesso) {
        return await ProcessoApi.recuperarEtiquetas(identificadorProcesso);
    }

    async adicionarEtiqueta(processo: Processo, textoNovaEtiqueta: string, login: string) {
        const identificadorProcesso = processo.identificadorProcesso;
        if (!processo.etiquetaJaExiste(textoNovaEtiqueta)) {
            let etiqueta;
            const todasAsTags = GabineteStore.etiquetasParaAutoComplete;
            if (todasAsTags && todasAsTags.length > 0) {
                etiqueta = todasAsTags.find(tag => tag.texto.toUpperCase().localeCompare(textoNovaEtiqueta.toUpperCase()) === 0);
            }
            await ProcessoApi.adicionarEtiqueta(identificadorProcesso, textoNovaEtiqueta, login, etiqueta ? etiqueta.numero : undefined);
            this.carregarEtiquetasProcessoPorId(identificadorProcesso);
            runInAction(() => {
                MensagemStore.apresentarMensagem(`A etiqueta "${textoNovaEtiqueta}" foi adicionada ao processo.`);
            });
        }
        return false;
    }

    async adicionarEtiquetaEmLote(idsProcessos, textoNovaEtiqueta: string, login: string) {
        let etiqueta;
        const listaIdsProcessos = []

        idsProcessos.forEach(processo => {
            listaIdsProcessos.push(processo.identificadorProcesso)
            if (!processo.etiquetaJaExiste(textoNovaEtiqueta)) {
                const todasAsTags = GabineteStore.etiquetasParaAutoComplete;
                if (todasAsTags && todasAsTags.length > 0) {
                    etiqueta = todasAsTags.find(tag => tag.texto.toUpperCase().localeCompare(textoNovaEtiqueta.toUpperCase()) === 0);
                }
                this.carregarEtiquetasProcessoPorId(processo.identificadorProcesso);
                runInAction(() => {
                    MensagemStore.apresentarMensagem(`A etiqueta "${textoNovaEtiqueta}" foi adicionada ao processo.`);
                });
            }
        })

        const etiquetas = [{
            texto: textoNovaEtiqueta,
            cpf: login,
            numero: etiqueta ? etiqueta.numero : undefined
        }];

        await ProcessoApi.adicionarEtiquetaEmLote(listaIdsProcessos, etiquetas);
        return false;
    }


    async carregarRetornoProcessosAsync(processos: Processo[]): Promise<void> {
        const idsProcessos = processos.map(p => p.identificadorProcesso)
        InformacoesProcessoService.consultaRetornosProcessos(idsProcessos).then(retornos => {
            this.aplicarATodosOsProcessos(retornos, (processo, valor) => {
                processo.retorno = processo.retorno || valor || false;
            });
        }).catch(exception => {
            if (!apresentarApiErroPadrao(exception)) {
                MensagemStore.apresentarErro("Erro ao carregar os retornos ao gabinete");
            }
        })
    }

    async carregarRetornoProcessosAsyncPorIdsProcessos(idsProcessos: number[]): Promise<void> {

        InformacoesProcessoService.consultaRetornosProcessos(idsProcessos).then(retornos => {
            this.aplicarAosProcessos(idsProcessos, retornos, (processo, valor) => {
                processo.retorno = valor || false;
            });
        }).catch(exception => {
            if (!apresentarApiErroPadrao(exception)) {
                MensagemStore.apresentarErro("Erro ao carregar os retornos ao gabinete");
            }
        })
    }

    async carregarRetornoProcesso(processo: Processo): Promise<void> {
        runInAction(() => {
            ProcessoStore.carregandoRetornos = true;
        });
        InformacoesProcessoService.consultaRetornoProcesso(processo.identificadorProcesso).then(retornos => {
            runInAction(() => {
                processo.retorno = retornos.get(processo.identificadorProcesso) || false;
                ProcessoStore.carregandoRetornos = false;
            });
        }).catch(exception => {
            if (!apresentarApiErroPadrao(exception)) {
                MensagemStore.apresentarErro("Erro ao carregar os retornos ao gabinete");
            }
        })
    }

    async carregarRetornoProcessosTodosAsync(): Promise<void> {
        runInAction(() => {
            ProcessoStore.carregandoRetornos = true;
        });

        InformacoesProcessoService.consultaRetornosProcessos(this.idsProcessos).then(retornos => {
            runInAction(() => {
                ProcessoStore._processos.forEach(processo => {
                    processo.retorno = retornos[processo.identificadorProcesso] || false;
                })
                ProcessoStore.carregandoRetornos = false;
                ProcessoStore.carregouRetornos = true;
            });
        });
    }

    async carregarPartesProcesso(processo: Processo): Promise<void> {
        const partesProcesso = await InformacoesProcessoService.consultarPartesProcesso(processo.identificadorProcesso);
        runInAction(() => {
            processo.partes = partesProcesso;
        });
    }

    async buscarPartesProcessoFiltro() {
        const partes = await ProcessoApi.buscarPartesProcessoFiltro()
        return partes;
    }

    carregarPartesDeTodosOsProcessos = async (): Promise<void> => {
        runInAction(() => {
            ProcessoStore.carregandoPartesDeProcesso = true;
            ProcessoStore.carregouPartes = true;
        });
        const partesProcesso = await InformacoesProcessoService.consultarPartesDaListaDeProcesso(this.idsProcessos);
        runInAction(() => {
            ProcessoStore._processos.forEach(processo => {
                processo.partes = partesProcesso.get(processo.identificadorProcesso);
            })
            ProcessoStore.carregandoPartesDeProcesso = false;
        });
    }

    carregarUltimoHistoricoSituacaoMinuta = async (): Promise<void> => {
        runInAction(() => {
            ProcessoStore.carregandoHistoricoMinutasProcesso = true;
            ProcessoStore.carregouHistoricoMinutas = true;
        });
        const minutasList: number[] = [];
        //Pega o numero das minutas de todos os processo
        ProcessoStore._processos.forEach(proc => {
            if (!proc.minutas) {
                return;
            }
            proc.minutas.forEach((minuta) => {
                if (minuta) {
                    minutasList.push(minuta.numero);
                }
            })
        })

        const result = await MinutaService.listarUltimoHistoricoSituacaoMinuta(minutasList);
        runInAction(() => {
            FiltroStore.historicoMinuta = result;
            ProcessoStore.carregandoHistoricoMinutasProcesso = false;
        });
    }

    async retirarProcessoDoGe(numerosProcesso: string[], cpfResponsavel = '', localizacao = '', motivo = '') {
        return await ProcessoApi.retirarProcessoDoGe(numerosProcesso, cpfResponsavel, localizacao, motivo);
    }

    async carregarAssuntosProcesso(processo: Processo): Promise<void> {
        const assuntos = await AssuntoService.buscarPorProcessos([processo.identificadorProcesso]);
        runInAction(() => {
            ProcessoStore._processos
                .find(p => p.identificadorProcesso === processo.identificadorProcesso)
                .assuntosProcesso = assuntos[processo.identificadorProcesso];
        });
    }

    async carregarAssuntosDeTodosOsProcessosAsync(): Promise<void> {
        runInAction(() => {
            ProcessoStore.carregandoAssuntos = true;
        });
        const idsProcessos = ProcessoStore._processos.map(proc => proc.identificadorProcesso);
        AssuntoService.buscarPorProcessos(idsProcessos).then(assuntos => {
            this.aplicarATodosOsProcessos(assuntos, (processo, valor) => {
                processo.assuntosProcesso = valor || [];
            });
            runInAction(() => {
                ProcessoStore.carregandoAssuntos = false;
                ProcessoStore.carregouAssuntos = true;
            });
        });
    }

    async carregarAssuntosDeTodosOsProcessosAsyncPorIdsProcessos(idsProcessos: number[]): Promise<void> {
        AssuntoService.buscarPorProcessos(idsProcessos).then(assuntos => {
            this.aplicarAosProcessos(idsProcessos, assuntos, (processo, valor) => {
                processo.assuntosProcesso = valor || [];
            });
        });
    }

    get idsProcessos(): number[] {
        return ProcessoStore._processos.map(p => p.identificadorProcesso);
    }

    private idsPrimeirosProcessos(): number[] {
        return ProcessoStore._processos.slice(0, QTD_PROCESSOS_INICIAIS).map(p => p.identificadorProcesso);
    }

    private idsRestantes(): number[] {
        return ProcessoStore._processos.slice(QTD_PROCESSOS_INICIAIS, ProcessoStore._processos.length).map(p => p.identificadorProcesso);
    }

    async carregarEtiquetasProcesso(processo: Processo): Promise<void> {
        const etiquetasDoProcesso = await this.recuperarEtiquetas(processo.identificadorProcesso);
        runInAction(() => {
            processo.etiquetas = etiquetasDoProcesso as IEtiqueta[] || [];
        });
    }

    async carregarComentariosProcesso(processo: Processo): Promise<void> {
        const minutas = await ComentariosMinutaService.buscarMinutasComentadasDeTodosOsProcessos([processo.identificadorProcesso]);

        runInAction(() => {
            const procCarregar = ProcessoStore._processos.find(proc => proc.identificadorProcesso === processo.identificadorProcesso)
            procCarregar.idsMinutasComentadas = minutas[processo.identificadorProcesso] || [];
        });
    }

    async carregarProcessosFavoritos(): Promise<void> {
        ProcessoApi.recuperarProcessosFavoritos().then(favoritos => {
            this.aplicarATodosOsProcessos(favoritos, (processo, valor) => {
                processo.favorito = valor;
            });
        });
    }

    async carregarProcessosFavoritosPorIdsProcessos(idsProcessos: number[]): Promise<void> {
        runInAction(() => {
            ProcessoStore._processos
                .filter(proc => idsProcessos.includes(proc.identificadorProcesso))
                .filter(proc => UsuarioStore.processosFavoritosUsuario[proc.identificadorProcesso])
                .forEach(proc => proc.favorito = true);
        });
    }

    async recuperarTriagemLivre(idsProcessos: number[]) {
        return ProcessoApi.recuperarTriagemLivreDeProcessos(idsProcessos);
    }

    async carregarTriagemLivreProcesso(processo: Processo): Promise<void> {
        const triagemLivreDoProcesso = await this.recuperarTriagemLivre([processo.identificadorProcesso]);
        runInAction(() => {
            processo.triagemLivre = triagemLivreDoProcesso[processo.identificadorProcesso];
        });
    }


    async carregarEtiquetasDeTodosOsProcessos(): Promise<void> {
        const etiquetas = await this.buscarEmLotes<IEtiqueta[]>(this.idsProcessos, lote => ProcessoApi.recuperarEtiquetasDeTodosOsProcessos(lote));
        this.aplicarATodosOsProcessos(etiquetas, (processo, valor) => {
            processo.etiquetas = valor || [];
        });
    }

    async carregarEtiquetasPorIdsProcessos(idsProcessos: number[]): Promise<void> {
        const processoEtiquetaMap = await ProcessoApi.recuperarEtiquetasDeTodosOsProcessos(idsProcessos);
        runInAction(() => {
            processoEtiquetaMap.forEach((etiquetas, idProcesso) => {
                const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso)
                processo.etiquetas = etiquetas || [];
            })
        });
    }

    async carregarTriagemTodosProcessos(): Promise<void> {
        const processosTriagens = await this.buscarEmLotes<any>(this.idsProcessos, lote => TriagemService.buscarTriagemDeTodosOsProcessos(lote));
        this.aplicarATodosOsProcessos(processosTriagens, (processo, valor) => {
            processo.triagem = valor as any || [];
        });
    }

    async carregarTemasTodosProcessos(): Promise<void> {
        const processosTriagens = await this.buscarEmLotes<any>(this.idsProcessos, lote => TriagemService.buscarTemasDeTodosOsProcessos(lote));
        this.aplicarATodosOsProcessos(processosTriagens, (processo, valor) => {
            processo.temas = valor as any || [];
        });
    }

    async carregarTriagemLivrePorIdsProcessos(idsProcessos: number[]): Promise<void> {
        const processoTriagemLivreMap = await this.recuperarTriagemLivre(idsProcessos);
        runInAction(() => {
            processoTriagemLivreMap.forEach((triagemLivre, idProcesso) => {
                const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso)
                processo.triagemLivre = triagemLivre;
            })
        });
    }

    async carregarTriagensLivreDeTodosOsProcessos(): Promise<void> {
        const triagens = await this.buscarEmLotes<string>(this.idsProcessos, lote => ProcessoApi.recuperarTriagemLivreDeProcessos(lote));
        this.aplicarATodosOsProcessos(triagens, (processo, valor) => {
            processo.triagemLivre = valor;
        });
    }

    async carregarMinutasComentadasDeTodosOsProcessos(): Promise<void> {
        const comentariosMinuta = await this.buscarEmLotes<any>(this.idsProcessos, lote => ComentariosMinutaService.buscarMinutasComentadasDeTodosOsProcessos(lote));
        this.aplicarATodosOsProcessos(comentariosMinuta, (processo, valor) => {
            processo.idsMinutasComentadas = valor as any || [];
        });
    }

    async carregarMinutasComentadasPorIdsProcessos(idsProcessos: number[]): Promise<void> {
        const processoMinutasMap = await ComentariosMinutaService.buscarMinutasComentadasDeTodosOsProcessos(idsProcessos);
        runInAction(() => {
            processoMinutasMap.forEach((minutas, idProcesso) => {
                const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso)
                processo.idsMinutasComentadas = minutas;
            })
        });
    }

    async carregarLiberacaoVotos(): Promise<void> {
        const idsProcessosComVoto = []
        const processosComVoto = ProcessoStore._processos.filter((processo: Processo) => {
            const voto = processo.minutas.find(minuta => minuta.isTipo(TipoMinuta.VOTO));
            if (voto) {
                idsProcessosComVoto.push(processo.identificadorProcesso);
                return true;
            }
            return false;
        });
        MinutaService.consultaLiberacaoVoto2(idsProcessosComVoto).then(idsProcessoLiberados => {
            processosComVoto.forEach(proc => {
                if (idsProcessoLiberados[proc.identificadorProcesso]) {
                    const voto = proc.minutas.find(minuta => minuta.isTipo(TipoMinuta.VOTO));
                    runInAction(() => {
                        if (voto) {
                            voto.votoJaliberado = true;
                        }
                    });
                }
            });
        })
    }

    async carregarLiberacaoVotosPorIdsProcessos(idsProcessos: number[]): Promise<void> {
        const processosComVoto = []
        ProcessoStore._processos.forEach((processo: Processo) => {
            const voto = processo.minutas.find(minuta => minuta.isTipo(TipoMinuta.VOTO));
            if (voto) {
                processosComVoto.push(processo.identificadorProcesso);
            }
        });

        const idsProcessosComVoto = ProcessoStore._processos
            .filter(proc => idsProcessos.includes(proc.identificadorProcesso))
            .filter(proc => proc.minutas.find(minuta => minuta.isTipo(TipoMinuta.VOTO)))
            .map(proc => proc.identificadorProcesso)

        const processosComVotoLiberadoMap = await MinutaService.consultaLiberacaoVoto2(idsProcessosComVoto);
        processosComVotoLiberadoMap.forEach((votoLiberado, idProcesso) => {
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso)
            const minutaVoto = processo.minutas.find(minuta => minuta.isTipo(TipoMinuta.VOTO));
            if (minutaVoto) {
                minutaVoto.votoJaliberado = votoLiberado;
            }
        })
    }

    async carregarEtiquetasDaOrigem(idsProcessosLista: number[]): Promise<void> {
        const idsProcessos = idsProcessosLista ? idsProcessosLista : ProcessoStore._processos.map(proc => proc.identificadorProcesso);
        const ids2 = chunk(idsProcessos, 800);
        ids2.forEach(async ids => {
            const etiquetasOrigem = await ProcessoApi.consultaEtiquetasDaOrigem(ids);
            runInAction(() => {
                ProcessoStore._processos.filter(proc => etiquetasOrigem[proc.identificadorProcesso]).forEach((processo: Processo) => {
                    processo.etiquetasOrigem = etiquetasOrigem[processo.identificadorProcesso];
                    const indicadores = [];
                    processo.indicadores.forEach(ind => indicadores[ind.descricao] = ind);
                    processo.etiquetasOrigem.forEach(etO => indicadores[etO.nome] = { descricao: etO.nome, id: undefined, origem: undefined, isEtiqueta: true });
                    processo.indicadores = this.comIndicadorSegredoDeJustica(processo, Object.values(indicadores));
                });
            });
        })
    }

    async carregarIndicadoresProcesso(processo: Processo): Promise<void> {
        const indicadoresProcesso = await InformacoesProcessoService.consultarIndicadores(processo.identificadorProcesso);
        runInAction(() => {
            processo.indicadores = this.comIndicadorSegredoDeJustica(processo, indicadoresProcesso as IIndicadores[]);
        });
    }

    carregarIndicadoresDeTodosOsProcessosAsync = async () => {

        runInAction(() => {
            ProcessoStore.carregandoIndicadores = true;
        });
        const idsProcessos = ProcessoStore._processos.map(proc => proc.identificadorProcesso);
        InformacoesProcessoService.buscarIndicadoresDaListaDeProcessos(idsProcessos).then(indicadores => {
            this.aplicarATodosOsProcessos(indicadores, (processo, valor) => {
                processo.indicadores = this.comIndicadorSegredoDeJustica(processo, valor || []);
            });
            runInAction(() => {
                ProcessoStore.carregandoIndicadores = false;
                ProcessoStore.carregouIndicadores = true;
            });
            this.carregarEtiquetasDaOrigem(undefined);
        });
    }

    async carregarIndicadoresDeTodosOsProcessosAsyncPorIdsProcessos(idsProcessos: number[]): Promise<void> {

        InformacoesProcessoService.buscarIndicadoresDaListaDeProcessos(idsProcessos).then(indicadores => {
            this.aplicarAosProcessos(idsProcessos, indicadores, (processo, valor) => {
                const ind = valor || [];
                ind.forEach(i => {
                    i.isEtiqueta = false;
                })
                processo.indicadores = this.comIndicadorSegredoDeJustica(processo, ind);
            });
            this.carregarEtiquetasDaOrigem(idsProcessos);
        });

    }

    async carregarEtiquetasProcessoPorId(identificadorProcesso: number) {
        const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === identificadorProcesso);
        const etiquetasDoProcesso = (await this.recuperarEtiquetas(identificadorProcesso)) as IEtiqueta[];
        runInAction(() => {
            processo.etiquetas = etiquetasDoProcesso || [];
        });
    }

    async carregarUnidadesPublicacao(idProcesso: number) {
        if (!idProcesso) {
            return [];
        }
        return await ProcessoApi.carregarUnidadesPublicacao(idProcesso);
    }

    async carregarTodasUnidadesPublicacao() {
        return await ProcessoApi.carregarTodasUnidadesPublicacao();
    }

    buscarProcessoNoGabinete = async (numeroProcesso: {
        numero: string;
        digito: string;
        ano: string;
        orgao: string;
        regional: string;
        vara: string
    }): Promise<boolean> => {

        if (!ProcessoStore.carregouProcessosNoEscaninho) {
            when(() => ProcessoStore.carregouProcessosNoEscaninho == true, () => {
                this.buscarProcessoNoGabinete(numeroProcesso);
            });
            return false;
        }

        const numeroNormalizado = numeroProcesso.numero
            ? numeroProcesso.numero.padStart(7, '0')
            : '';

        const isCompleto =
            numeroNormalizado.length === 7 &&
            numeroProcesso.digito?.length === 2 &&
            numeroProcesso.ano?.length === 4 &&
            numeroProcesso.orgao?.length === 1 &&
            numeroProcesso.regional?.length === 2 &&
            numeroProcesso.vara?.length === 4;

        //Busca parcial somente no escaninho
        if (!isCompleto) {

            const numeroDigitado = numeroProcesso.numero?.replace(/^0+/, '') || '';

            const processosEncontrados = ProcessoStore._processos.filter(p => {

                const numeroObj = UtilNumeroProcesso.matchObjetoNumeroProcesso(p.numeroProcesso);

                const numeroSemZeros = numeroObj.numero.replace(/^0+/, '');

                return (
                    (!numeroDigitado || numeroSemZeros.startsWith(numeroDigitado)) &&

                    (!numeroProcesso.digito || numeroObj.digito.startsWith(numeroProcesso.digito)) &&
                    (!numeroProcesso.ano || numeroObj.ano.startsWith(numeroProcesso.ano)) &&
                    (!numeroProcesso.orgao || numeroObj.justica.startsWith(numeroProcesso.orgao)) &&
                    (!numeroProcesso.regional || numeroObj.tribunal.startsWith(numeroProcesso.regional)) &&
                    (!numeroProcesso.vara || numeroObj.vara.startsWith(numeroProcesso.vara))
                );
            });

            if (processosEncontrados.length === 1) {
                this.mostraProcessoNoEscaninho(processosEncontrados[0]);
                return true;
            } else if (processosEncontrados.length > 1) {

                const aplicarFiltro = () => {
                    runInAction(() => {
                        FiltroService.fazerBuscaPadrao();
                        FiltroStore.numeroProcessoFilter = numeroDigitado;
                        FiltroStore.digitoProcessoFilter = numeroProcesso.digito || '';
                        FiltroStore.anoProcessoFilter = numeroProcesso.ano || '';
                        FiltroStore.orgaoProcessoFilter = numeroProcesso.orgao || '';
                        FiltroStore.regionalProcessoFilter = numeroProcesso.regional || '';
                        FiltroStore.varaProcessoFilter = numeroProcesso.vara || '';
                        FiltroStore.procurar(false);
                    });
                };

                if (RouterStore.isAlgumEscaninho) {
                    aplicarFiltro();
                } else {
                    RouterStore.push('/escaninho/gerencial');

                    setTimeout(() => {
                        aplicarFiltro();
                    }, 0);
                }
                return true;
            } else {
                MensagemStore.apresentarAviso("Nenhum processo encontrado no gabinete.");
                return false;
            }
        }

        //Busca completa, tenta encontrar no escaninho antes de buscar externamente
        const numeroDeProcesso =
            numeroNormalizado + '-' +
            numeroProcesso.digito + '.' +
            numeroProcesso.ano + '.' +
            numeroProcesso.orgao + '.' +
            numeroProcesso.regional + '.' +
            numeroProcesso.vara;

        try {

            const processo = this.retornaProcessoNaStore(numeroDeProcesso);

            if (!processo) {
                const id = await this.idProcessoPorNumero(numeroDeProcesso);
                return await this.procurarProcessoExterno(id);
            } else {
                this.mostraProcessoNoEscaninho(processo);
                return true;
            }

        } catch (error) {
            if (!apiErroPadraoJaExibido(error) && !apresentarApiErroPadrao(error)) {
                console.error('Erro ao buscar processo no gabinete:', error);
                MensagemStore.apresentarAviso(
                    "Processo não existe. Por favor verifique o número digitado e tente novamente ou contate o administrador do sistema."
                );
            }
            return false;
        }
    }

    mostraProcessoNoEscaninho = (processo: Processo) => {
        if (processo) {
            if (RouterStore.isAlgumEscaninho) {
                runInAction(() => {
                    FiltroService.fazerBuscaPadrao();
                    const foraFluxo = !processo.noFluxoCerto;

                    const numeroProcesso = UtilNumeroProcesso.matchObjetoNumeroProcesso(processo.numeroProcesso);
                    FiltroStore.numeroProcessoFilter = numeroProcesso.numero;
                    FiltroStore.digitoProcessoFilter = numeroProcesso.digito;
                    FiltroStore.anoProcessoFilter = numeroProcesso.ano;
                    FiltroStore.orgaoProcessoFilter = numeroProcesso.justica;
                    FiltroStore.regionalProcessoFilter = numeroProcesso.tribunal;
                    FiltroStore.varaProcessoFilter = numeroProcesso.vara;
                    FiltroStore.foraDoGabineteFilter = !!foraFluxo;
                    FiltroStore.procurar(false);
                });
            } else {
                runInAction(() => {
                    const foraFluxo = !processo.noFluxoCerto;
                    RouterStore.push('/escaninho/gerencial?idProcesso=' + processo.identificadorProcesso + (foraFluxo ? '&foraDoFluxo=true' : ''));
                });
            }
        }
        return processo;
    }

    procuraProcessoNoGabinete = async (id: number) => {
        const processo = await this.recuperaPorSearch({ gabinete: UsuarioStore.gabinete, idsProcessos: [id] });
        if (processo?.length > 0) {
            if (RouterStore.isAlgumEscaninho) {
                runInAction(() => {
                    const foraFluxo = !processo[0].noFluxoCerto;

                    const numeroProcesso = UtilNumeroProcesso.matchObjetoNumeroProcesso(processo[0].numeroProcesso);

                    FiltroStore.numeroProcessoFilter = numeroProcesso.numero;
                    FiltroStore.digitoProcessoFilter = numeroProcesso.digito;
                    FiltroStore.anoProcessoFilter = numeroProcesso.ano;
                    FiltroStore.orgaoProcessoFilter = numeroProcesso.justica;
                    FiltroStore.regionalProcessoFilter = numeroProcesso.tribunal;
                    FiltroStore.varaProcessoFilter = numeroProcesso.vara;
                    FiltroStore.foraDoGabineteFilter = !!foraFluxo;

                    FiltroStore.procurar(false);
                });
            } else {
                runInAction(() => {
                    const foraFluxo = !processo[0].noFluxoCerto;
                    RouterStore.push('/escaninho/gerencial?idProcesso=' + id + (foraFluxo ? '&foraDoFluxo=true' : ''));
                });
            }
        }
        return processo?.length > 0;
    }

    carregarEmentaDecisao = async (voto: Minuta) => {
        const idProcesso = voto.idProcesso;
        const ementaDecisao = await ProcessoApi.recuperarEmentaDecisao(idProcesso);
        if (ementaDecisao[idProcesso].decisao === undefined && ementaDecisao[idProcesso].dispositivo !== undefined) {
            ementaDecisao[idProcesso].decisao = ementaDecisao[idProcesso].dispositivo
        }
        ementaDecisao[idProcesso].ementa = ementaDecisao[idProcesso].ementa ? ementaDecisao[idProcesso].ementa.trim() : '';
        ementaDecisao[idProcesso].decisao = ementaDecisao[idProcesso].decisao ? ementaDecisao[idProcesso].decisao.trim() : '';
        return ementaDecisao[idProcesso];
    }

    procurarProcessoExterno = async (id): Promise<boolean> => {
        const processo = await this.recuperaProcessoPorId(id);
        if (processo.segredoDeJustica && processo.relator?.sigla !== UsuarioStore.gabinete) {
            MensagemStore.apresentarAviso('Processo em segredo de justiça. Favor consultar este processo no sistema de origem.')
            return false;
        } else {
            RouterStore.abrirJanela('/espelhoProcesso/' + id);
            return true;
        }
    }
    salvarTriagemLivre(processos: Processo[], textoTriagemLivre: string) {
        ProcessoApi.salvarTriagemLivre(processos.map(proc => proc.identificadorProcesso), textoTriagemLivre).then(_result => {
            MensagemStore.apresentarMensagem('Triagem livre salva');

            processos.forEach((proc) => {
                runInAction(() => {
                    proc.triagemLivre = textoTriagemLivre;
                })
            });
        }).catch(erro => {
            if (!apresentarApiErroPadrao(erro)) {
                MensagemStore.apresentarErro('Não foi possível salvar a triagem livre para o processo. Erro: ' + erro)
            }
        });
    }

    salvarFavorito(processo: Processo) {

        ProcessoApi.salvarProcessosFavorito(processo.identificadorProcesso).then(() => {
            runInAction(() => {
                processo.favorito = true;
            })
        }).catch(error => {
            if (!apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarErro(`Erro ao salvar favorito`);
            }
        })
    }

    async removerFavorito(processo: Processo) {
        ProcessoApi.retirarProcessosFavorito(processo.identificadorProcesso).then(() => {
            runInAction(() => {
                processo.favorito = false;
            })
        }).catch(error => {
            if (!apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarErro(`Erro ao remover o favorito`);
            }
        });

    }

    abrirPagina = (sistema, processo) => {
        if (sistema == 'pje') {
            RouterStore.abrirJanela(Ambiente.urlPJe + processo.identificadorProcesso + '/detalhe');
        } else if (sistema == 'esij') {
            const numeroProcesso = NumeroProcesso.valueOf(processo.numeroProcesso);
            const numProc = numeroProcesso.getNumero();//temp[1];
            const digito = numeroProcesso.getDigito() //temp2[0];
            const anoProc = numeroProcesso.getAno() //temp2[1];
            const justica = numeroProcesso.getJustica() //temp2[2];
            const numTribunal = numeroProcesso.getTribunal() //temp2[3];
            const numVara = numeroProcesso.getVara() //temp2[4];

            RouterStore.abrirJanela(Ambiente.urlEsij + '/esij/VisualizarPecas.do?loadPorNumProcFormatado=1&numProc=' + numProc +
                '&digito=' + digito +
                '&anoProc=' + anoProc +
                '&justica=' + justica +
                '&numTribunal=' + numTribunal +
                '&numVara=' + numVara);
        }
    }

    deveEsmaecer(processo: Processo) {
        return !processo.noFluxoCerto;
    }

}

export default new ProcessoService();
